import { useState, useRef, useEffect } from 'react';
import { ShieldCheck, TrendingUp, ArrowDownCircle, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import { useShieldBalance, useShieldTransactions } from '../hooks/useShield';
import type { ShieldTransactionType } from '../api/shield.api';

// ─── Animated balance counter ─────────────────────────────────────────────────

function AnimatedBalance({ value }: { value: number }) {
  const [displayed, setDisplayed] = useState(value);
  const prev = useRef(value);

  useEffect(() => {
    if (prev.current === value) return;
    const start = prev.current;
    const end = value;
    const duration = 800;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(start + (end - start) * eased);
      if (progress < 1) requestAnimationFrame(tick);
      else prev.current = end;
    };
    requestAnimationFrame(tick);
  }, [value]);

  return (
    <span>
      ₹{displayed.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
}

// ─── Type filter tabs ─────────────────────────────────────────────────────────

type FilterTab = 'all' | ShieldTransactionType;

const TABS: { value: FilterTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'contribution', label: 'Contributions' },
];

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  accent: 'green' | 'red' | 'slate';
}) {
  const accentMap = {
    green: 'text-emerald-400 bg-emerald-400/10',
    red: 'text-red-400 bg-red-400/10',
    slate: 'text-slate-300 bg-slate-600/30',
  };

  return (
    <div className="rounded-xl border border-slate-700/50 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-5 backdrop-blur-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
        <span className={`rounded-lg p-1.5 ${accentMap[accent]}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <p className="font-mono text-2xl font-bold tracking-tight text-slate-100">{value}</p>
    </div>
  );
}

// ─── Transaction row ──────────────────────────────────────────────────────────

function TxTypeBadge({ type }: { type: ShieldTransactionType }) {
  return type === 'contribution' ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-400 ring-1 ring-emerald-400/20">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
      Contribution
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-400/10 px-2.5 py-0.5 text-xs font-semibold text-red-400 ring-1 ring-red-400/20">
      <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
      Withdrawal
    </span>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ZiptoShieldPage() {
  const [filter, setFilter] = useState<FilterTab>('all');
  const [page, setPage] = useState(1);

  const { data: ledger, isLoading: ledgerLoading, refetch } = useShieldBalance();
  const { data: txData, isLoading: txLoading } = useShieldTransactions({
    type: filter === 'all' ? undefined : filter,
    page,
    limit: 20,
  });

  const balance = Number(ledger?.balance ?? 0);
  const totalContributed = Number(ledger?.total_contributed ?? 0);
  const totalTx = txData?.total ?? 0;

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100">

      {/* ── Header row ── */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-500">Admin · Treasury</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-100">Bookfleet Shield Fund</h1>
          <p className="mt-0.5 text-sm text-slate-500">₹1 collected per delivery — managed by admin only</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Hero balance card ── */}
      <div className="relative mb-6 overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-slate-900 via-emerald-950/30 to-slate-900 p-8 shadow-2xl shadow-emerald-900/30">
        {/* Decorative glow */}
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-10 left-1/3 h-40 w-40 rounded-full bg-emerald-500/5 blur-2xl" />

        <div className="relative flex flex-col items-center py-4 text-center">
          {/* Shield icon with ring pulse */}
          <div className="relative mb-6">
            <div className="absolute inset-0 animate-ping rounded-full bg-emerald-500/10" style={{ animationDuration: '3s' }} />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 shadow-lg shadow-emerald-900/40">
              <ShieldCheck className="h-10 w-10 text-emerald-400" strokeWidth={1.5} />
            </div>
          </div>

          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-500/80">
            Current Balance
          </p>

          {ledgerLoading ? (
            <div className="my-2 flex h-16 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <p className="font-mono text-6xl font-black tracking-tight text-emerald-400 drop-shadow-lg">
              <AnimatedBalance value={balance} />
            </p>
          )}

          <p className="mt-2 text-sm text-slate-500">
            Last updated:{' '}
            {ledger?.updated_at ? formatDate(ledger.updated_at) : '—'}
          </p>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2">
        <StatCard
          label="Total Collected"
          value={`₹${totalContributed.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
          icon={TrendingUp}
          accent="green"
        />
        <StatCard
          label="Total Transactions"
          value={totalTx.toLocaleString('en-IN')}
          icon={ArrowDownCircle}
          accent="slate"
        />
      </div>

      {/* ── Transaction history ── */}
      <div className="rounded-xl border border-slate-700/50 bg-slate-900/80 backdrop-blur-sm">
        {/* Filter tabs + title */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-700/60 px-6 py-4">
          <h2 className="text-sm font-semibold text-slate-200">Transaction History</h2>
          <div className="flex overflow-hidden rounded-lg border border-slate-700 bg-slate-800">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => { setFilter(tab.value); setPage(1); }}
                className={`px-4 py-1.5 text-xs font-semibold transition-colors ${
                  filter === tab.value
                    ? 'bg-emerald-600 text-white'
                    : 'text-slate-400 hover:bg-slate-700 hover:text-slate-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {txLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner />
          </div>
        ) : !txData?.items?.length ? (
          <EmptyState title="No transactions yet" description="Shield contributions will appear here after the first completed delivery." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700/60 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Balance After</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Reference</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txData.items.map((tx) => (
                  <TableRow key={tx.id} className="border-slate-700/40 transition-colors hover:bg-slate-800/40">
                    <TableCell>
                      <TxTypeBadge type={tx.type} />
                    </TableCell>
                    <TableCell>
                      <span
                        className={`font-mono text-sm font-bold ${
                          tx.type === 'contribution' ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {tx.type === 'contribution' ? '+' : '-'}₹
                        {Number(tx.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm text-slate-300">
                        ₹{Number(tx.balance_after).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </TableCell>
                    <TableCell>
                      {tx.type === 'contribution' && tx.booking_id ? (
                        <span className="font-mono text-xs text-slate-500" title={tx.booking_id}>
                          #{tx.booking_id.slice(0, 8)}…
                        </span>
                      ) : tx.notes ? (
                        <span className="max-w-[180px] truncate text-xs text-slate-400" title={tx.notes}>
                          {tx.notes}
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDate(tx.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {(txData.pages ?? 1) > 1 && (
              <div className="flex items-center justify-between border-t border-slate-700/60 px-6 py-3">
                <p className="text-xs text-slate-500">
                  Page <span className="text-slate-300">{page}</span> of{' '}
                  <span className="text-slate-300">{txData.pages}</span>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100 disabled:opacity-30"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(txData.pages, p + 1))}
                    disabled={page === txData.pages}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100 disabled:opacity-30"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}